import {
  report,
  StatChannel,
  StatReportType,
  StatErrorLevel,
  StatErrorCategory,
} from '../neo';
import { env } from '../common/env';
import { hasSessionStorage, setSessionStorage } from '../common/utils';
import {
  BizCids,
  OneClickPayModalBids,
  CidMap,
  GuideModuleMetricKeys,
} from './biz-constants';
import {
  BridgeBids,
  BridgeMetricKeys,
  OfflineMetricKeys,
  OfflineBids,
  OneClickPayModalProcessBids,
  OneClickPayModalProcessMetricKeys,
  AjaxBids,
  AjaxRaptorBids,
} from './tech-constants';

export { Cid } from './tech-constants';
class StatHelper {
  basicParams: any = {};

  lxParams: any = {};

  init(commonParams: any) {
    this.basicParams = {
      app_name: env.appName,
      nb_container: env.nbContainer,
      network_env: env.network,
      ...commonParams,
    };
    this.lxParams = { ...this.basicParams };
  }

  /**
   * 设置基础的通用参数
   * @param data 参数隧道
   */
  setBasicParams(data: CashierParams) {
    this.basicParams = {
      app_version: data.nb_appversion || '',
      nb_platform: data.device_platform || '',
      is_debug: data.is_debug || 0,
      nb_version: data.nb_version || '',
      hybrid_mtpay_verison: env.webAppVersion,
      hybrid_current_scheme: window.location.href,
      hybrid_current_url: env.hybridCurrentUrl,
      ...this.basicParams,
    };

    this.lxParams = {
      device_rooted: data.device_rooted || 0,
      priority: 'normal',
      technology_log_version: this.basicParams.nb_version || '',
      unique_id: env.uniqueId,
      trade_no: data.tradeno || '', // 技术统计使用 trade_no
      tradeNo: data.tradeno || '', // 业务统计使用 tradeNo
      trans_id: data.trans_id || '',
      ...this.basicParams,
    };
  }

  setExtBasicParams(
    data: Partial<
      Record<
        'dispatcher_use_shark' | 'mtrect_abtest_grp' | 'retain_modal_type',
        string | number
      >
    >
  ) {
    this.basicParams = {
      ...this.basicParams,
      ...data,
    };
    this.lxParams = {
      ...this.lxParams,
      ...data,
    };
  }

  /**
   * 设置业务通用参数
   * @param data 业务通用参数
   */
  setBizCommonParams(data: Record<string, unknown>) {
    this.lxParams = {
      ...this.lxParams,
      ...data,
    };
  }

  /**
   * 上报灵犀业务埋点
   * @param bid bid
   * @param data 数据
   * @param type 埋点类型
   * @param once 是否上报一次
   */
  reportToLXBiz(
    cid: string,
    bid: string,
    data: any = {},
    type: StatReportType = StatReportType.VIEW,
    once: boolean = false
  ) {
    if (!cid) {
      throw new Error('reportToLXBiz(): cid 必填');
    }
    if (once) {
      const key = `${cid}-${bid}`;
      if (hasSessionStorage(key)) {
        return;
      }
      setSessionStorage(key);
    }
    console.log('reportToLXBiz(): cid:', cid);
    report(
      StatChannel.LXAnalytics,
      {
        ...this.lxParams,
        ...data,
      },
      {
        lxParams: {
          cid,
          bid,
          type,
        },
      }
    );
  }

  /**
   * 上报灵犀技术埋点
   * @param bid bid
   * @param data 数据
   * @param type 埋点类型
   * @param once 是否上报一次
   */
  reportToLX(
    cid: string,
    bid: string,
    data: any = {},
    type: StatReportType = StatReportType.SC,
    once: boolean = false
  ) {
    if (!cid) {
      throw new Error('reportToLX(): cid 必填');
    }
    if (once) {
      if (hasSessionStorage(bid)) {
        return;
      }
      setSessionStorage(bid);
    }
    report(
      StatChannel.LXAnalytics,
      {
        ...this.lxParams,
        ...data,
      },
      {
        lxParams: {
          cid,
          bid,
          type,
        },
      }
    );
  }

  /**
   * 上报到 Raptor
   * @param error 错误信息
   * @param tags 额外信息
   * @param level 错误等级, 默认为 info
   * @param category 错误类别, 默认为 JSError
   */
  reportToRaptor(
    error: any,
    tags: any = {},
    level: StatErrorLevel = StatErrorLevel.Info,
    category: StatErrorCategory = StatErrorCategory.JSError
  ) {
    report(
      StatChannel.Raptor,
      {},
      {
        raptorParams: {
          error,
          level,
          category,
          tags: {
            ...this.basicParams,
            ...tags,
          },
        },
      }
    );
  }

  /**
   * 上报自定义指标
   * @param key 纬度
   * @param value 数值
   * @param tags 其他的额度
   * @param isNative 是否上报到 native 通道
   */
  reportMetric(
    key: string,
    value: number = 1,
    tags: any = {},
    isNative = false
  ) {
    report(
      StatChannel.Metric,
      {},
      {
        metricParams: {
          key,
          value,
          tags: {
            ...this.basicParams,
            ...tags,
          },
          isNative,
        },
      }
    );
  }

  /**
   * 上报页面 PV
   */
  reportPV(bid = ''): void {
    report(
      StatChannel.LXAnalytics,
      {
        ...this.lxParams,
      },
      {
        lxParams: {
          cid: BizCids.OneClickPayModal,
          bid,
          type: StatReportType.PV,
        },
      }
    );
    // 用于观察实时数据
    this.reportToRaptor(
      'PageStart',
      {},
      StatErrorLevel.Info,
      StatErrorCategory.ResourceError
    );
  }

  // eslint-disable-next-line class-methods-use-this
  getAjaxUrlBid(url: string): {
    type: string;
    lx: { start: string; success: string; fail: string };
    cat: { start: string; success: string; fail: string };
  } | null {
    if (url.indexOf('openoneclickpay') > -1) {
      return {
        type: 'openoneclickpay',
        lx: {
          start: AjaxBids.OneclickpayOpenStart,
          success: AjaxBids.OneclickpayOpenSucc,
          fail: AjaxBids.OneclickpayOpenFail,
        },
        cat: {
          start: AjaxRaptorBids.OneclickpayOpenStart,
          success: AjaxRaptorBids.OneclickpayOpenSucc,
          fail: AjaxRaptorBids.OneclickpayOpenFail,
        },
      };
    }

    if (url.indexOf('adjustUserQuota') > -1) {
      return {
        type: 'adjustUserQuota',
        lx: {
          start: AjaxBids.AdjustQuotaStart,
          success: AjaxBids.AdjustQuotaSucc,
          fail: AjaxBids.AdjustQuotaFail,
        },
        cat: {
          start: AjaxRaptorBids.AdjustQuotaStart,
          success: AjaxRaptorBids.AdjustQuotaSucc,
          fail: AjaxRaptorBids.AdjustQuotaFail,
        },
      };
    }

    if (url.indexOf('deductordersave') > -1) {
      return {
        type: 'deductordersave',
        lx: {
          start: AjaxBids.DeductordersaveStart,
          success: AjaxBids.DeductordersaveSucc,
          fail: AjaxBids.DeductordersaveFail,
        },
        cat: {
          start: AjaxRaptorBids.DeductordersaveStart,
          success: AjaxRaptorBids.DeductordersaveSucc,
          fail: AjaxRaptorBids.DeductordersaveFail,
        },
      };
    }
    return null;
  }
}

export const statHelper = new StatHelper();
export {
  BizCids,
  OneClickPayModalBids,
  OneClickPayModalProcessBids,
  OneClickPayModalProcessMetricKeys,
  StatReportType,
  StatErrorLevel,
  StatErrorCategory,
  BridgeBids,
  BridgeMetricKeys,
  OfflineMetricKeys,
  OfflineBids,
  CidMap,
  GuideModuleMetricKeys,
};
